import {Loader2Icon, Trash2Icon, UndoIcon} from "lucide-react";
import {useMemo, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {deleteOp, purgeRevision, useVNextOps, type VNextOp} from "@hooks/use-vnext";

type FilterMode = "all" | "active" | "revisions" | "agent" | "human";

interface OpsTabProps {
    workspaceId: string;
    scenarioId: string;
}

const OP_TYPE_LABELS: Record<string, string> = {
    set_baseline: "Baseline",
    set_driver: "Driver",
    set_override: "Override",
    set_weekly_ovr: "Weekly Override",
};

function opTypeBadge(opType: string) {
    const label = OP_TYPE_LABELS[opType] ?? opType;
    if (opType === "set_weekly_ovr") {
        return <Badge className="bg-blue-100 text-blue-700 border-blue-300">{label}</Badge>;
    }
    const variant = opType === "set_driver" ? "default" : opType === "set_override" ? "secondary" : "outline";
    return <Badge variant={variant}>{label}</Badge>;
}

function createdByBadge(createdBy: string) {
    if (createdBy === "agent") {
        return <Badge className="bg-teal-100 text-teal-700 border-teal-300">Agent</Badge>;
    }
    return <Badge variant="secondary">Human</Badge>;
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
}

function descriptionFor(op: VNextOp): string {
    if (op.label) return op.label;
    const type = OP_TYPE_LABELS[op.op_type] ?? op.op_type;
    const target = op.target_node_label || op.target_category || "";
    return target ? `${type}: ${target}` : type;
}

function targetFor(op: VNextOp): string {
    return op.target_node_label || op.target_category || "\u2014";
}

function periodFor(op: VNextOp): string {
    if (op.op_type === "set_weekly_ovr") {
        return (op.params?.week_iso as string) || `W${op.params?.week_number ?? ""}`;
    }
    return op.target_month || "All";
}

export function OpsTab({workspaceId, scenarioId}: OpsTabProps) {
    const {ops, isLoading, error, refresh} = useVNextOps(workspaceId, scenarioId);
    const [filter, setFilter] = useState<FilterMode>("all");
    const [undoing, setUndoing] = useState<string | null>(null);
    const [purging, setPurging] = useState<string | null>(null);

    const activeOps = useMemo(() => ops.filter((o) => o.is_active), [ops]);
    const inactiveOps = useMemo(() => ops.filter((o) => !o.is_active), [ops]);

    const filteredActive = useMemo(() => {
        if (filter === "revisions") return [];
        if (filter === "agent") return activeOps.filter((o) => o.created_by === "agent");
        if (filter === "human") return activeOps.filter((o) => o.created_by === "human");
        return activeOps;
    }, [activeOps, filter]);

    const filteredInactive = useMemo(() => {
        if (filter === "active") return [];
        if (filter === "agent") return inactiveOps.filter((o) => o.created_by === "agent");
        if (filter === "human") return inactiveOps.filter((o) => o.created_by === "human");
        return inactiveOps;
    }, [inactiveOps, filter]);

    const handleUndo = async (opId: string) => {
        setUndoing(opId);
        try {
            await deleteOp(workspaceId, opId);
            refresh();
        } finally {
            setUndoing(null);
        }
    };

    const handlePurge = async (opId: string) => {
        setPurging(opId);
        try {
            await purgeRevision(workspaceId, opId);
            refresh();
        } finally {
            setPurging(null);
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent className="space-y-3">
                    {Array.from({length: 5}).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                    ))}
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">{error}</CardContent>
            </Card>
        );
    }

    return (
        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden" data-tour="ops-tab-surface">
            <CardHeader className="shrink-0 pb-3">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>Ops &amp; Revisions</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            {activeOps.length} active op{activeOps.length !== 1 ? "s" : ""}
                            {inactiveOps.length > 0 && ` · ${inactiveOps.length} revision${inactiveOps.length !== 1 ? "s" : ""}`}
                        </p>
                    </div>
                    <div data-tour="ops-filter">
                    <Select value={filter} onValueChange={(v) => setFilter(v as FilterMode)}>
                        <SelectTrigger className="w-[150px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Active only</SelectItem>
                            <SelectItem value="revisions">Revisions only</SelectItem>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="human">Human</SelectItem>
                        </SelectContent>
                    </Select>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-auto p-0" data-tour="ops-table">
                {filteredActive.length === 0 && filteredInactive.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12 text-sm">
                        No ops match the current filter.
                    </p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="pl-6">Description</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Target</TableHead>
                                <TableHead>Period</TableHead>
                                <TableHead>By</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right pr-6">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredActive.map((op) => (
                                <TableRow key={op.id}>
                                    <TableCell className="pl-6 font-medium">{descriptionFor(op)}</TableCell>
                                    <TableCell>{opTypeBadge(op.op_type)}</TableCell>
                                    <TableCell className="text-sm">{targetFor(op)}</TableCell>
                                    <TableCell className="text-sm">{periodFor(op)}</TableCell>
                                    <TableCell>{createdByBadge(op.created_by)}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{formatDate(op.created_at)}</TableCell>
                                    <TableCell className="text-right pr-6">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                            disabled={undoing === op.id}
                                            onClick={() => handleUndo(op.id)}
                                        >
                                            {undoing === op.id ? (
                                                <Loader2Icon className="size-3 animate-spin mr-1" />
                                            ) : (
                                                <UndoIcon className="size-3 mr-1" />
                                            )}
                                            Undo
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {filteredInactive.length > 0 && (
                                <>
                                    <TableRow>
                                        <TableCell colSpan={7} className="pl-6 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
                                            Revisions (deactivated)
                                        </TableCell>
                                    </TableRow>
                                    {filteredInactive.map((op) => (
                                        <TableRow key={op.id} className="opacity-60">
                                            <TableCell className="pl-6 font-medium line-through">{descriptionFor(op)}</TableCell>
                                            <TableCell>{opTypeBadge(op.op_type)}</TableCell>
                                            <TableCell className="text-sm">{targetFor(op)}</TableCell>
                                            <TableCell className="text-sm">{periodFor(op)}</TableCell>
                                            <TableCell>{createdByBadge(op.created_by)}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{formatDate(op.created_at)}</TableCell>
                                            <TableCell className="text-right pr-6">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-xs text-muted-foreground hover:text-red-600 hover:bg-red-50"
                                                    disabled={purging === op.id}
                                                    onClick={() => handlePurge(op.id)}
                                                >
                                                    {purging === op.id ? (
                                                        <Loader2Icon className="size-3 animate-spin mr-1" />
                                                    ) : (
                                                        <Trash2Icon className="size-3 mr-1" />
                                                    )}
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            )}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
